import type { DocumentShareLinkRecord } from '../services/documentShareLinks.service';

export type TransmissionArtefact = 'first' | 'final' | 'unknown';

export type TransmissionArtefactClassifyArgs = {
  firstWorkingVersionId?: string | null;
  finalWorkingVersionId?: string | null;
  /** Latest Final reapproval transmission version id (convenience). */
  finalReapprovalTransmissionVersionId?: string | null;
  /** All known Final reapproval transmission snapshot ids for this Final Survey. */
  finalTransmissionVersionIds?: string[] | null;
  /** All known Final reapproval share link ids. */
  finalShareLinkIds?: string[] | null;
  /**
   * Optional map of version id → survey kind, from document_versions.visual_survey_id
   * (or immediate parent of a transmission snapshot). Preferred when present.
   */
  versionSurveyKindById?: Record<string, 'first' | 'final' | string> | null;
};

/**
 * Classify a customer share link as First or Final Survey transmission.
 * Prefer write-time surveyArtefact, then Final reapproval pins, then survey-kind
 * on source/sent lineage, then working-id binds. Do not default ambiguous links to First.
 */
export function classifyTransmissionArtefact(
  link: DocumentShareLinkRecord,
  args: TransmissionArtefactClassifyArgs,
): TransmissionArtefact {
  const sent = String(link.sentVersionId || '').trim();
  const source = String(link.sourceVersionId || '').trim();
  const linkId = String(link.id || '').trim();
  const tagged = String(link.surveyArtefact || '').trim().toLowerCase();
  if (tagged === 'final') return 'final';
  if (tagged === 'first') return 'first';

  const first = String(args.firstWorkingVersionId || '').trim();
  const final = String(args.finalWorkingVersionId || '').trim();
  const finalTx = String(args.finalReapprovalTransmissionVersionId || '').trim();
  const finalTxIds = new Set(
    [...(args.finalTransmissionVersionIds || []), finalTx]
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  );
  const finalShareIds = new Set(
    [...(args.finalShareLinkIds || [])]
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  );
  const kindByVersion = args.versionSurveyKindById || {};

  if (linkId && finalShareIds.has(linkId)) return 'final';
  if (sent && finalTxIds.has(sent)) return 'final';

  const kindFromSource = source ? String(kindByVersion[source] || '').trim().toLowerCase() : '';
  const kindFromSent = sent ? String(kindByVersion[sent] || '').trim().toLowerCase() : '';
  // Prefer Final when either side says final (Final working is often cloned_from First).
  if (kindFromSource === 'final' || kindFromSent === 'final') return 'final';
  if (kindFromSource === 'first' || kindFromSent === 'first') return 'first';

  if (final && (source === final || sent === final)) return 'final';
  if (first && (source === first || sent === first)) return 'first';

  return 'unknown';
}

export function filterShareLinksForArtefact(
  links: DocumentShareLinkRecord[],
  artefact: 'first' | 'final' | 'all',
  args: TransmissionArtefactClassifyArgs,
): DocumentShareLinkRecord[] {
  if (artefact === 'all') return links;
  return links.filter((link) => classifyTransmissionArtefact(link, args) === artefact);
}
